import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api, { trackActivity, portfolioRegistryAPI } from '../services/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import './PortfolioComparisonApp.css';
import ManagedDisclaimer from '../components/ManagedDisclaimer';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const PORTFOLIO_COLOURS = [
  '#072040', '#599BE3', '#f0a500', '#91CF9B', '#874564',
  '#39AEC5', '#305496', '#FFC000', '#AA8090', '#011E41',
];
const getColour = (idx) => PORTFOLIO_COLOURS[idx % PORTFOLIO_COLOURS.length];

const PERFORMANCE_PERIODS = [
  '1 Month', '3 Month', '6 Month', '12 Month',
  '2 Year', '3 Year', '5 Year', 'Inception',
];

// ─── Collapsible Section ───────────────────────────────────────────
const CollapsibleSection = ({ title, expanded, onToggle, children }) => (
  <div className="collapsible-section">
    <div
      className={`collapsible-header${expanded ? ' expanded' : ''}`}
      onClick={onToggle}
    >
      <h3>{title}</h3>
      <span className="collapsible-toggle">{expanded ? '−' : '+'}</span>
    </div>
    {expanded && <div className="collapsible-body">{children}</div>}
  </div>
);

// ─── Main Component ────────────────────────────────────────────────
const PortfolioComparisonApp = () => {
  // Reference data
  const [portfolioList, setPortfolioList] = useState([]);
  const [blenderPortfolios, setBlenderPortfolios] = useState([]);
  const [registryData, setRegistryData] = useState(null);

  // Selections (unlimited portfolio codes)
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [filterTexts, setFilterTexts] = useState({});

  // Fetched comparison data
  const [performanceData, setPerformanceData] = useState(null);
  const [holdingsData, setHoldingsData] = useState(null);
  const [allocationData, setAllocationData] = useState({});
  const [driftData, setDriftData] = useState(null);
  const [riskMetrics, setRiskMetrics] = useState({});

  // Loading / error
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [blenderForbidden, setBlenderForbidden] = useState(false);
  const [error, setError] = useState(null);

  // Section collapse state
  const [sections, setSections] = useState({
    performance: true,
    allocation: true,
    drift: false,
    holdings: false,
    risk: false,
    stress: false,
  });

  // Track which drift asset-class rows are expanded
  const [expandedDriftRows, setExpandedDriftRows] = useState({});

  // ─── Registry mapping ─────────────────────────────────────────
  const codeToBlenderMap = useMemo(() => {
    if (!registryData?.suites) return {};
    const map = {};
    registryData.suites.forEach((suite) => {
      suite.portfolios.forEach((p) => {
        if (p.portfolio_code && p.blender_code) {
          map[p.portfolio_code] = p.blender_code;
        }
      });
    });
    return map;
  }, [registryData]);

  // Set of blender codes available in the blender engine
  const blenderCodeSet = useMemo(
    () => new Set(blenderPortfolios),
    [blenderPortfolios]
  );

  // Display name lookup
  const codeToName = useMemo(() => {
    const map = {};
    portfolioList.forEach((p) => {
      map[p.portfolio_code] = p.display_name || p.portfolio_name;
    });
    return map;
  }, [portfolioList]);

  // ─── Mount: load reference data ───────────────────────────────
  useEffect(() => {
    trackActivity('app_access', 'portfolio_comparison');
    Promise.all([
      api.get('/api/portfolio/portfolios'),
      api.get('/api/blender/available-portfolios').catch(() => ({ data: [] })),
      portfolioRegistryAPI.getRegistry('portfolio_viewer').catch(() => null),
    ])
      .then(([portfoliosRes, blenderRes, registryRes]) => {
        setPortfolioList(portfoliosRes.data);
        setBlenderPortfolios(blenderRes.data);
        if (registryRes) setRegistryData(registryRes);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading portfolios:', err);
        setError('Failed to load portfolios. Please check the backend is running.');
        setLoading(false);
      });
  }, []);

  // ─── Fetch comparison data when selection changes ─────────────
  useEffect(() => {
    if (selectedCodes.length === 0) {
      setPerformanceData(null);
      setHoldingsData(null);
      setAllocationData({});
      setDriftData(null);
      setRiskMetrics({});
      return;
    }

    setDataLoading(true);
    const codesParam = selectedCodes.join(',');

    // Parallel fetches for portfolio viewer data
    const perfPromise = api
      .get('/api/portfolio/portfolios/performance', {
        params: { portfolio_codes: codesParam },
      })
      .then((r) => r.data)
      .catch(() => null);

    const holdingsPromise = api
      .get('/api/portfolio/portfolios/holdings', {
        params: { portfolio_codes: codesParam },
      })
      .then((r) => r.data)
      .catch(() => null);

    // Allocation: one call per portfolio
    const allocPromises = selectedCodes.map((code) =>
      api
        .get(`/api/portfolio/portfolios/${code}/allocation`)
        .then((r) => ({ code, data: r.data }))
        .catch(() => ({ code, data: null }))
    );

    // Drift data (target vs floating weights)
    const driftPromise = api
      .get('/api/portfolio/portfolios/drift', {
        params: { portfolio_codes: codesParam },
      })
      .then((r) => r.data)
      .catch(() => null);

    // Blender risk metrics: one call per eligible portfolio
    const riskPromises = selectedCodes.map((code) => {
      const blenderCode = codeToBlenderMap[code];
      if (!blenderCode || !blenderCodeSet.has(blenderCode)) {
        return Promise.resolve({ code, data: null, status: 'no_blender' });
      }
      return api
        .post('/api/blender/calculate-blend', {
          weights: { [blenderCode]: 1.0 },
        })
        .then((r) => ({ code, data: r.data, status: 'ok' }))
        .catch((err) => {
          if (err.response?.status === 403) {
            return { code, data: null, status: 'forbidden' };
          }
          return { code, data: null, status: 'error' };
        });
    });

    Promise.all([
      perfPromise,
      holdingsPromise,
      Promise.all(allocPromises),
      driftPromise,
      Promise.allSettled(riskPromises),
    ]).then(([perf, holdings, allocResults, drift, riskResults]) => {
      setPerformanceData(perf);
      setHoldingsData(holdings);
      setDriftData(drift);

      // Build allocation map
      const allocMap = {};
      allocResults.forEach(({ code, data }) => {
        if (data) allocMap[code] = data;
      });
      setAllocationData(allocMap);

      // Build risk map and check for 403
      const riskMap = {};
      let anyForbidden = false;
      riskResults.forEach((result) => {
        const val = result.status === 'fulfilled' ? result.value : null;
        if (val) {
          if (val.status === 'forbidden') anyForbidden = true;
          if (val.status === 'ok') riskMap[val.code] = val.data;
        }
      });
      setRiskMetrics(riskMap);
      setBlenderForbidden(anyForbidden);
      setDataLoading(false);
    });
  }, [selectedCodes, codeToBlenderMap, blenderCodeSet]);

  // ─── Selection handlers ───────────────────────────────────────
  const handleSelect = useCallback(
    (code) => {
      if (!code) return;
      setSelectedCodes((prev) => [...prev, code]);
      setFilterTexts({});
    },
    []
  );

  const handleRemove = useCallback((slotIndex) => {
    setSelectedCodes((prev) => prev.filter((_, i) => i !== slotIndex));
  }, []);

  const handleFilterChange = useCallback((text) => {
    setFilterTexts({ add: text });
  }, []);

  const toggleSection = useCallback((key) => {
    setSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const toggleDriftRow = useCallback((ac) => {
    setExpandedDriftRows((prev) => ({ ...prev, [ac]: !prev[ac] }));
  }, []);

  // ─── Filtered dropdown options (exclude already selected) ─────
  const getFilteredOptions = useCallback(
    () => {
      const filterText = (filterTexts.add || '').toLowerCase();
      return portfolioList.filter((p) => {
        if (selectedCodes.includes(p.portfolio_code)) return false;
        if (filterText) {
          const name = (p.display_name || p.portfolio_name || '').toLowerCase();
          const code = p.portfolio_code.toLowerCase();
          return name.includes(filterText) || code.includes(filterText);
        }
        return true;
      });
    },
    [portfolioList, selectedCodes, filterTexts]
  );

  // ─── Format helpers ───────────────────────────────────────────
  const fmtPct = (val) => {
    if (val === null || val === undefined) return '—';
    return `${Number(val).toFixed(2)}%`;
  };

  const fmtPct1 = (val) => {
    if (val === null || val === undefined) return '—';
    return `${Number(val).toFixed(1)}%`;
  };

  // ─── Render: Loading ──────────────────────────────────────────
  if (loading) {
    return (
      <div className="comparison-container">
        <div className="data-loading-overlay">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="comparison-container">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  // ─── Build performance chart data ─────────────────────────────
  const perfChartData = () => {
    if (!performanceData?.portfolios?.length) return null;
    const labels = PERFORMANCE_PERIODS;
    const datasets = performanceData.portfolios.map((pf, idx) => ({
      label: codeToName[pf.portfolio_code] || pf.portfolio_name,
      data: labels.map((period) => pf.performance?.[period]?.portfolio ?? null),
      backgroundColor: getColour(idx),
      borderRadius: 3,
    }));
    return { labels, datasets };
  };

  // ─── Build allocation chart data ──────────────────────────────
  const allocChartData = () => {
    if (!selectedCodes.length) return null;
    // Collect all asset classes across selected portfolios
    const assetClassSet = new Set();
    selectedCodes.forEach((code) => {
      (allocationData[code] || []).forEach((row) =>
        assetClassSet.add(row.asset_class_group)
      );
    });
    const labels = Array.from(assetClassSet);
    if (!labels.length) return null;

    const datasets = selectedCodes.map((code, idx) => {
      const allocMap = {};
      (allocationData[code] || []).forEach((row) => {
        allocMap[row.asset_class_group] = row.target_percent;
      });
      return {
        label: codeToName[code] || code,
        data: labels.map((ac) => allocMap[ac] ?? 0),
        backgroundColor: getColour(idx),
        borderRadius: 3,
      };
    });

    return { labels, datasets };
  };

  const chartOptions = (titleText, indexAxis = 'x') => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis,
    plugins: {
      legend: { position: 'top', labels: { font: { size: 11 } } },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${fmtPct(ctx.raw)}`,
        },
      },
    },
    scales: {
      x: {
        ticks: { font: { size: 10 } },
        grid: { display: indexAxis === 'y' },
      },
      y: {
        ticks: {
          font: { size: 10 },
          callback: (v) => (indexAxis === 'x' ? `${v}%` : v),
        },
        grid: { display: indexAxis === 'x' },
      },
    },
  });

  // ─── Render ───────────────────────────────────────────────────
  return (
    <div className="comparison-container">
      {/* Header */}
      <div className="comparison-header mb-4">
        <h2>Portfolio Comparison</h2>
        <p className="text-muted">Compare portfolios side by side</p>
      </div>

      {/* Portfolio Selector */}
      <div className="selector-row">
        {/* Selected portfolio cards */}
        {selectedCodes.map((code, idx) => (
          <div
            key={code}
            className="selector-card active"
            style={{ borderColor: getColour(idx) }}
          >
            <div className="selector-card-header">
              <span
                className="selector-card-label"
                style={{ backgroundColor: getColour(idx) }}
              >
                Portfolio {idx + 1}
              </span>
              <button
                className="remove-btn"
                onClick={() => handleRemove(idx)}
                title="Remove"
              >
                ×
              </button>
            </div>
            <div className="selected-name">
              {codeToName[code] || code}
            </div>
            <div className="selected-code">{code}</div>
          </div>
        ))}

        {/* Add portfolio card — shown when unselected portfolios remain */}
        {selectedCodes.length < portfolioList.length && (
          <div className="selector-card">
            <div className="selector-card-header">
              <span
                className="selector-card-label"
                style={{ backgroundColor: getColour(selectedCodes.length) }}
              >
                + Add
              </span>
            </div>
            <input
              type="text"
              className="filter-input"
              placeholder="Search portfolios..."
              value={filterTexts.add || ''}
              onChange={(e) => handleFilterChange(e.target.value)}
            />
            <select
              value=""
              onChange={(e) => handleSelect(e.target.value)}
            >
              <option value="">Select a portfolio...</option>
              {getFilteredOptions().map((p) => (
                <option key={p.portfolio_code} value={p.portfolio_code}>
                  {p.display_name || p.portfolio_name} ({p.portfolio_code})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* No selection prompt */}
      {selectedCodes.length === 0 && (
        <div className="no-selection-prompt">
          <p>Select at least one portfolio above to begin comparing.</p>
        </div>
      )}

      {/* Data loading indicator */}
      {dataLoading && selectedCodes.length > 0 && (
        <div className="data-loading-overlay">
          <div className="spinner-border text-primary me-2" role="status" />
          <span>Loading comparison data...</span>
        </div>
      )}

      {/* Content (only when we have selections and aren't loading) */}
      {selectedCodes.length > 0 && !dataLoading && (
        <>
          {/* Summary Cards */}
          {holdingsData?.summary && (
            <div className="summary-cards-row">
              {selectedCodes.map((code, idx) => {
                const summary = holdingsData.summary[code];
                return (
                  <div className="summary-card" key={code}>
                    <div
                      className="summary-card-header"
                      style={{ backgroundColor: getColour(idx) }}
                    >
                      {codeToName[code] || code}
                    </div>
                    <div className="summary-card-body">
                      <div className="summary-metric">
                        <span className="summary-metric-label">Growth Exposure</span>
                        <span className="summary-metric-value">
                          {summary ? fmtPct1(summary.growth_exposure) : '—'}
                        </span>
                      </div>
                      <div className="summary-metric">
                        <span className="summary-metric-label">Defensive Exposure</span>
                        <span className="summary-metric-value">
                          {summary ? fmtPct1(summary.defensive_exposure) : '—'}
                        </span>
                      </div>
                      <div className="summary-metric">
                        <span className="summary-metric-label">Expense Ratio</span>
                        <span className="summary-metric-value">
                          {summary ? fmtPct(summary.expense_ratio) : '—'}
                        </span>
                      </div>
                      <div className="summary-metric">
                        <span className="summary-metric-label">FX Exposure</span>
                        <span className="summary-metric-value">
                          {summary ? fmtPct1(summary.fx_exposure) : '—'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ─── Performance Section ───────────────────────────── */}
          <CollapsibleSection
            title="Performance"
            expanded={sections.performance}
            onToggle={() => toggleSection('performance')}
          >
            {performanceData?.portfolios?.length ? (
              <>
                <div className="comparison-table-container">
                  <table className="comparison-table">
                    <thead>
                      <tr>
                        <th>Period</th>
                        {performanceData.portfolios.map((pf, idx) => (
                          <th
                            key={pf.portfolio_code}
                            style={{
                              backgroundColor: getColour(idx),
                            }}
                          >
                            {codeToName[pf.portfolio_code] || pf.portfolio_name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {PERFORMANCE_PERIODS.map((period) => (
                        <React.Fragment key={period}>
                          {/* Portfolio return row */}
                          <tr className="perf-portfolio-row">
                            <td>{period}</td>
                            {performanceData.portfolios.map((pf) => (
                              <td key={pf.portfolio_code}>
                                {fmtPct(pf.performance?.[period]?.portfolio)}
                              </td>
                            ))}
                          </tr>
                          {/* AMI benchmark row */}
                          <tr className="perf-ami-row">
                            <td style={{ paddingLeft: 24, fontSize: '0.75rem' }}>
                              AMI Benchmark
                            </td>
                            {performanceData.portfolios.map((pf) => (
                              <td key={pf.portfolio_code}>
                                {fmtPct(pf.performance?.[period]?.ami)}
                              </td>
                            ))}
                          </tr>
                          {/* Difference row */}
                          <tr className="perf-diff-row">
                            <td style={{ paddingLeft: 24, fontSize: '0.75rem' }}>
                              Difference
                            </td>
                            {performanceData.portfolios.map((pf) => {
                              const diff = pf.performance?.[period]?.difference;
                              const cls =
                                diff > 0
                                  ? 'diff-positive'
                                  : diff < 0
                                  ? 'diff-negative'
                                  : '';
                              return (
                                <td key={pf.portfolio_code} className={cls}>
                                  {diff !== null && diff !== undefined
                                    ? `${diff > 0 ? '+' : ''}${fmtPct(diff)}`
                                    : '—'}
                                </td>
                              );
                            })}
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Performance bar chart */}
                {perfChartData() && (
                  <div className="comparison-chart-container">
                    <Bar
                      data={perfChartData()}
                      options={chartOptions('Performance Comparison')}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="section-message">
                No performance data available.
              </div>
            )}
          </CollapsibleSection>

          {/* ─── Allocation Section ────────────────────────────── */}
          <CollapsibleSection
            title="Allocation"
            expanded={sections.allocation}
            onToggle={() => toggleSection('allocation')}
          >
            {Object.keys(allocationData).length > 0 ? (
              <>
                <div className="comparison-table-container">
                  <table className="comparison-table">
                    <thead>
                      <tr>
                        <th>Asset Class</th>
                        {selectedCodes.map((code, idx) => (
                          <th
                            key={code}
                            style={{
                              backgroundColor: getColour(idx),
                            }}
                          >
                            {codeToName[code] || code}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Collect all unique asset classes
                        const acSet = new Set();
                        selectedCodes.forEach((code) => {
                          (allocationData[code] || []).forEach((row) =>
                            acSet.add(row.asset_class_group)
                          );
                        });
                        return Array.from(acSet).map((ac) => (
                          <tr key={ac}>
                            <td>{ac}</td>
                            {selectedCodes.map((code) => {
                              const row = (allocationData[code] || []).find(
                                (r) => r.asset_class_group === ac
                              );
                              return (
                                <td key={code}>
                                  {row ? fmtPct1(row.target_percent) : '—'}
                                </td>
                              );
                            })}
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>

                {/* Vertical grouped bar chart */}
                {allocChartData() && (
                  <div className="comparison-chart-container">
                    <Bar
                      data={allocChartData()}
                      options={chartOptions('Allocation Comparison')}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="section-message">
                No allocation data available.
              </div>
            )}
          </CollapsibleSection>

          {/* ─── Drift Section (Floating vs Target) ────────────── */}
          <CollapsibleSection
            title="Weight Drift (Floating vs Target)"
            expanded={sections.drift}
            onToggle={() => toggleSection('drift')}
          >
            {driftData?.drift?.length ? (
              <div className="comparison-table-container">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Asset Class</th>
                      {selectedCodes.map((code, idx) => (
                        <th
                          key={code}
                          colSpan={3}
                          style={{ backgroundColor: getColour(idx) }}
                        >
                          {codeToName[code] || code}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      <th></th>
                      {selectedCodes.map((code) => (
                        <React.Fragment key={code}>
                          <th style={{ fontSize: '0.7rem', backgroundColor: '#1a3a5c' }}>Target</th>
                          <th style={{ fontSize: '0.7rem', backgroundColor: '#1a3a5c' }}>Floating</th>
                          <th style={{ fontSize: '0.7rem', backgroundColor: '#1a3a5c' }}>Drift</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {driftData.drift.map((row) => {
                      const ac = row.asset_class_group;
                      const isExpanded = !!expandedDriftRows[ac];
                      const hasHoldings = row.holdings?.length > 0;
                      return (
                        <React.Fragment key={ac}>
                          {/* Asset class row */}
                          <tr
                            className="asset-class-header-row"
                            style={{ cursor: hasHoldings ? 'pointer' : 'default' }}
                            onClick={() => hasHoldings && toggleDriftRow(ac)}
                          >
                            <td>
                              {hasHoldings && (
                                <span style={{ display: 'inline-block', width: 16, fontSize: '0.65rem', color: '#6c757d', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'none' }}>
                                  ▶
                                </span>
                              )}
                              {ac}
                            </td>
                            {selectedCodes.map((code) => {
                              const pd = row.portfolios?.[code];
                              if (!pd) {
                                return (
                                  <React.Fragment key={code}>
                                    <td>—</td><td>—</td><td>—</td>
                                  </React.Fragment>
                                );
                              }
                              const driftVal = pd.drift;
                              const driftCls = driftVal > 0.01 ? 'diff-positive' : driftVal < -0.01 ? 'diff-negative' : '';
                              return (
                                <React.Fragment key={code}>
                                  <td>{fmtPct(pd.target)}</td>
                                  <td>{fmtPct(pd.floating)}</td>
                                  <td className={driftCls}>
                                    {driftVal !== 0 ? `${driftVal > 0 ? '+' : ''}${fmtPct(driftVal)}` : '0.00%'}
                                  </td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                          {/* Expanded holding rows */}
                          {isExpanded && row.holdings?.map((h) => (
                            <tr key={`${ac}-${h.asset_name}`} style={{ backgroundColor: '#fafafa' }}>
                              <td className="holding-name-cell" style={{ fontSize: '0.75rem', color: '#555' }}>
                                {h.asset_name}
                              </td>
                              {selectedCodes.map((code) => {
                                const pd = h.portfolios?.[code];
                                if (!pd) {
                                  return (
                                    <React.Fragment key={code}>
                                      <td>—</td><td>—</td><td>—</td>
                                    </React.Fragment>
                                  );
                                }
                                const driftVal = pd.drift;
                                const driftCls = driftVal > 0.01 ? 'diff-positive' : driftVal < -0.01 ? 'diff-negative' : '';
                                return (
                                  <React.Fragment key={code}>
                                    <td style={{ fontSize: '0.75rem' }}>{fmtPct(pd.target)}</td>
                                    <td style={{ fontSize: '0.75rem' }}>{fmtPct(pd.floating)}</td>
                                    <td className={driftCls} style={{ fontSize: '0.75rem' }}>
                                      {driftVal !== 0 ? `${driftVal > 0 ? '+' : ''}${fmtPct(driftVal)}` : '0.00%'}
                                    </td>
                                  </React.Fragment>
                                );
                              })}
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="section-message">
                No drift data available.
              </div>
            )}
          </CollapsibleSection>

          {/* ─── Holdings Section ──────────────────────────────── */}
          <CollapsibleSection
            title="Holdings"
            expanded={sections.holdings}
            onToggle={() => toggleSection('holdings')}
          >
            {holdingsData?.holdings?.length ? (
              <div className="comparison-table-container" style={{ maxHeight: 600, overflowY: 'auto' }}>
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Holding</th>
                      <th style={{ minWidth: 100 }}>Asset Class</th>
                      {selectedCodes.map((code, idx) => (
                        <th
                          key={code}
                          style={{ backgroundColor: getColour(idx) }}
                        >
                          {codeToName[code] || code}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Group holdings by asset class
                      const groups = {};
                      holdingsData.holdings.forEach((h) => {
                        const ac = h.asset_class_group || 'Other';
                        if (!groups[ac]) groups[ac] = [];
                        groups[ac].push(h);
                      });

                      const rows = [];
                      Object.entries(groups).forEach(([ac, holdings]) => {
                        // Asset class header
                        rows.push(
                          <tr key={`ac-${ac}`} className="asset-class-header-row">
                            <td colSpan={2 + selectedCodes.length}>{ac}</td>
                          </tr>
                        );

                        // Individual holdings
                        holdings.forEach((h) => {
                          rows.push(
                            <tr key={h.asset_name + h.tr_code}>
                              <td className="holding-name-cell">
                                {h.asset_name}
                              </td>
                              <td style={{ fontSize: '0.75rem', color: '#666' }}>
                                {ac}
                              </td>
                              {selectedCodes.map((code) => (
                                <td key={code}>
                                  {h.weights?.[code] != null
                                    ? fmtPct(h.weights[code])
                                    : '—'}
                                </td>
                              ))}
                            </tr>
                          );
                        });

                        // Subtotal
                        const subtotals = selectedCodes.map((code) => {
                          const total = holdings.reduce(
                            (sum, h) => sum + (h.weights?.[code] || 0),
                            0
                          );
                          return total;
                        });
                        rows.push(
                          <tr key={`sub-${ac}`} className="asset-class-subtotal-row">
                            <td colSpan={2}>
                              {ac} Total
                            </td>
                            {subtotals.map((t, idx) => (
                              <td key={selectedCodes[idx]}>{fmtPct1(t)}</td>
                            ))}
                          </tr>
                        );
                      });

                      return rows;
                    })()}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="section-message">No holdings data available.</div>
            )}
          </CollapsibleSection>

          {/* ─── Risk Metrics Section ──────────────────────────── */}
          <CollapsibleSection
            title="Risk Metrics"
            expanded={sections.risk}
            onToggle={() => toggleSection('risk')}
          >
            {blenderForbidden && Object.keys(riskMetrics).length === 0 ? (
              <div className="section-message">
                Risk analytics require Portfolio Blender access.
              </div>
            ) : (
              <div className="comparison-table-container">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      {selectedCodes.map((code, idx) => (
                        <th
                          key={code}
                          style={{ backgroundColor: getColour(idx) }}
                        >
                          {codeToName[code] || code}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Expected Return', key: 'return', fmt: (v) => fmtPct((v * 100).toFixed(2)), isTop: true },
                      { label: 'Volatility', key: 'volatility', fmt: (v) => fmtPct((v * 100).toFixed(2)), isTop: true },
                      { label: 'Return / Risk', key: 'return_risk_ratio', fmt: (v) => v?.toFixed(2) },
                      { label: 'Sortino Ratio', key: 'sortino_ratio', fmt: (v) => v?.toFixed(2) },
                      { label: 'Calmar Ratio', key: 'calmar_ratio', fmt: (v) => v?.toFixed(2) },
                      { label: 'Avg Max Drawdown', key: 'avg_max_drawdown', fmt: (v) => fmtPct((v * 100).toFixed(2)) },
                      { label: 'Worst Max Drawdown', key: 'worst_max_drawdown', fmt: (v) => fmtPct((v * 100).toFixed(2)), tooltip: 'The single largest peak-to-trough decline observed across all 10,000 simulations — a roughly 1-in-10,000 outcome and therefore extremely unlikely.' },
                      { label: 'VaR 95%', key: 'var_95', fmt: (v) => fmtPct((v * 100).toFixed(2)) },
                      { label: 'CVaR 95%', key: 'cvar_95', fmt: (v) => fmtPct((v * 100).toFixed(2)) },
                    ].map((metric) => (
                      <tr key={metric.key}>
                        <td title={metric.tooltip || undefined} style={metric.tooltip ? { cursor: 'help' } : undefined}>{metric.label}</td>
                        {selectedCodes.map((code) => {
                          const data = riskMetrics[code];
                          if (!data) {
                            return (
                              <td key={code} className="not-available">
                                Not available
                              </td>
                            );
                          }
                          // Top-level fields vs stochastics.metrics
                          let val;
                          if (metric.isTop) {
                            val = data[metric.key];
                          } else {
                            val = data.stochastics?.metrics?.[metric.key];
                          }
                          return (
                            <td key={code}>
                              {val !== null && val !== undefined
                                ? metric.fmt(val)
                                : '—'}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CollapsibleSection>

          {/* ─── Stress Tests Section ──────────────────────────── */}
          <CollapsibleSection
            title="Stress Tests"
            expanded={sections.stress}
            onToggle={() => toggleSection('stress')}
          >
            {blenderForbidden && Object.keys(riskMetrics).length === 0 ? (
              <div className="section-message">
                Risk analytics require Portfolio Blender access.
              </div>
            ) : (
              (() => {
                // Collect all unique stress events across portfolios
                const eventsMap = new Map();
                selectedCodes.forEach((code) => {
                  const data = riskMetrics[code];
                  if (data?.stress_test_results) {
                    data.stress_test_results.forEach((event) => {
                      if (!eventsMap.has(event.event_name)) {
                        eventsMap.set(event.event_name, {
                          event_name: event.event_name,
                          start_date: event.start_date,
                          end_date: event.end_date,
                        });
                      }
                    });
                  }
                });

                const events = Array.from(eventsMap.values());

                if (events.length === 0) {
                  return (
                    <div className="section-message">
                      No stress test data available.
                    </div>
                  );
                }

                return (
                  <div className="comparison-table-container">
                    <table className="comparison-table">
                      <thead>
                        <tr>
                          <th>Event</th>
                          <th>Period</th>
                          {selectedCodes.map((code, idx) => (
                            <th
                              key={code}
                              style={{
                                backgroundColor: getColour(idx),
                              }}
                            >
                              {codeToName[code] || code}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((event) => (
                          <tr key={event.event_name}>
                            <td>{event.event_name}</td>
                            <td style={{ fontSize: '0.75rem', color: '#666' }}>
                              {event.start_date} – {event.end_date}
                            </td>
                            {selectedCodes.map((code) => {
                              const data = riskMetrics[code];
                              if (!data?.stress_test_results) {
                                return (
                                  <td key={code} className="not-available">
                                    Not available
                                  </td>
                                );
                              }
                              const match = data.stress_test_results.find(
                                (e) => e.event_name === event.event_name
                              );
                              if (!match) {
                                return <td key={code}>—</td>;
                              }
                              const ret = match.portfolio_return;
                              const cls =
                                ret < 0
                                  ? 'stress-negative'
                                  : ret > 0
                                  ? 'stress-positive'
                                  : '';
                              return (
                                <td key={code} className={cls}>
                                  {fmtPct((ret * 100).toFixed(2))}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()
            )}
          </CollapsibleSection>
        </>
      )}

      {/* Disclaimer Card */}
      <div className="card disclaimer-card mt-3">
        <div className="card-body">
          <div className="disclaimer-text">
            <h6><i className="bi bi-info-circle"></i> Portfolio Comparison &ndash; Important Information</h6>
            <ManagedDisclaimer dkey="tool_comparison" as="p" className="small text-muted mb-0" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioComparisonApp;
